import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Canvas, ShaderMaterialProps } from '@react-three/fiber';
import { useFrame, useThree } from '@react-three/fiber';
import { createMorphGeometry } from '@arkturian/wave-engine/core/MorphGeometry';
import { XRWrapper, XRBackground, ARButton } from '@arkturian/wave-engine/xr/XRProvider';
import * as THREE from 'three';
import { StoreApi, UseBoundStore } from 'zustand';
import gsap from 'gsap';
import Panel from '../components/ui/Panel';
import ResponsivePanelContainer from '../components/ui/ResponsivePanelContainer';
import { UniversalUIPage } from './UniversalUIPage';
import type { GPUParticleStoreState, GPUParticleStoreActions } from '../stores/useGPUParticleStore';
import DebugBBoxes from '@arkturian/wave-engine/camera/DebugBBoxes';
import DataIntelligencePanel from '../components/DataIntelligencePanel';
import { DataIntelligenceController } from '@arkturian/wave-engine/data/DataIntelligenceController';
import { InteractionController } from '@arkturian/wave-engine/interaction/InteractionController';
import ClickPickerBridge from '../components/ClickPickerBridge';
import LayouterSettingsOverlay from '../components/LayouterSettingsOverlay';
import TileModeButtons from '../components/TileModeButtons';
import { ParticleAnimator } from '@arkturian/wave-engine/core/ParticleAnimator';
import { createUniforms, applyShaderToMaterial } from '@arkturian/wave-engine/core/MorphShader';
import { useParticleSelection } from '@arkturian/wave-engine/core/ParticleSelectionManager';
import { MorphShape } from '@arkturian/wave-engine/core/MorphShape';
import { LinesAAMesh } from '@arkturian/wave-engine/lines';
import { setLines } from '@arkturian/wave-engine/lines';
import type { LineSegment } from '@arkturian/wave-engine/lines';
import CameraLight from '@arkturian/wave-engine/camera/CameraLight';
import SmoothZoomControls from '@arkturian/wave-engine/camera/SmoothZoomControls';
import LodManager from '@arkturian/wave-engine/atlas/LodManager';
import { bakeCurrentToOld } from '@arkturian/wave-engine/core/BufferTransition';
import { EngineRuntime } from '@arkturian/wave-engine/core/EngineRuntime';
import { CameraIntelligence } from '@arkturian/wave-engine/camera/CameraIntelligence';
import { InstancedMeshTileAccessor } from '@arkturian/wave-engine/camera/TileAccessor';
import BucketFilterLinker from '../components/BucketFilterLinker';
import BucketButtons3D from '../components/BucketButtons3D';
import type { Bucket } from '@arkturian/wave-engine/data/DataIntelligenceController';
import { ParticleInfoCard, InfoCardModeSwitcher } from '../components/infocard';

const MAX_PARTICLES = 50000;

// Centralised runtime singleton — replaces all module-level mutable state
const runtime = EngineRuntime.getInstance();

// (flyTargetRef removed — all camera navigation now flows through CameraIntelligence)

type StoreType = UseBoundStore<StoreApi<GPUParticleStoreState & GPUParticleStoreActions>>;

interface GPUParticlePageProps {
    store: StoreType;
    autoLoadOneal?: boolean;
}

const GPUParticleInstances: React.FC<{ store: StoreType, meshRef: React.RefObject<THREE.InstancedMesh>, uniformsRef: React.MutableRefObject<any> }> = ({ store, meshRef, uniformsRef }) => {
    const particleCount = store((state) => state.particleCount);
    const initializeParticles = store((state) => state.initializeParticles);
    const initialize = store((state) => state.initialize);
    const unmount = store((state) => state.unmount);

    const layoutShape = store((state) => state.layoutShape);
    const layoutMode = store((state) => state.layoutMode);
    const shapeSize = store((state) => state.shapeSize);
    const particleSize = store((state) => state.particleSize);
    const galleryRedux = store((state) => state.galleryRedux);
    const tileAspect = store((state) => state.tileAspect);
    const boxRatio = store((state) => state.boxRatio);
    const gapFactor = store((state) => state.gapFactor);
    const tileAspectMode = store((state) => state.tileAspectMode);
    const tileFixedAspect = store((state) => state.tileFixedAspect);

    // Library layout settings — synced to EngineRuntime for layout functions
    const layoutOrientation = store((state) => state.layoutOrientation);
    const layoutUpMode = store((state) => state.layoutUpMode);
    const layoutSpacingMode = store((state) => state.layoutSpacingMode);
    const layoutSpacing = store((state) => state.layoutSpacing);
    const spiralTurns = store((state) => state.spiralTurns);
    const spiralGrowthRate = store((state) => state.spiralGrowthRate);

    const isInitialMount = useRef(true);
    const prevCountRef = useRef(particleCount);
    const isTransitioning = useRef(false);
    const lastLayoutKey = useRef('');

    // Sync library layout settings from store to EngineRuntime
    useEffect(() => {
        runtime.layoutOrientation = layoutOrientation;
        runtime.layoutUpMode = layoutUpMode;
        runtime.layoutSpacingMode = layoutSpacingMode;
        runtime.layoutSpacing = layoutSpacing;
        runtime.spiralTurns = spiralTurns;
        runtime.spiralGrowthRate = spiralGrowthRate;
        runtime.tileAspectMode = tileAspectMode;
        runtime.tileFixedAspect = tileFixedAspect;
    }, [layoutOrientation, layoutUpMode, layoutSpacingMode, layoutSpacing, spiralTurns, spiralGrowthRate, tileAspectMode, tileFixedAspect]);

    const color1 = store((s) => s.color1);
    const color2 = store((s) => s.color2);
    const colorMix = store((s) => s.colorMix);
    const roughness = store((s) => s.roughness);
    const metalness = store((s) => s.metalness);
    const emissive = store((s) => s.emissive);
    const emissiveIntensity = store((s) => s.emissiveIntensity);

    const cornerRadius = store((s) => s.cornerRadius);

    // Base geometry + all per-instance morph attributes (engine factory, incl. aColor/aOldColor since 0.2)
    const baseGeometry = useMemo(
        () => createMorphGeometry({ maxInstances: MAX_PARTICLES, cornerRadius }),
        [cornerRadius],
    );

    const materialRef = useRef<THREE.MeshStandardMaterial>(null!);
    const { gl } = useThree();
    const particleAnimator = useMemo(() => {
        const pa = new ParticleAnimator();
        runtime.particleAnimator = pa;
        return pa;
    }, []);

    const uniforms = useMemo(() => {
        const u = createUniforms();
        // ParticleAnimator owns its own uniform arrays — share the same references
        const animUniforms = particleAnimator.getUniforms();
        u.uAnimSlots = animUniforms.uAnimSlots;
        u.uAnimOffsets = animUniforms.uAnimOffsets;
        u.uAnimPhysics = animUniforms.uAnimPhysics;
        return u;
    }, []);

    uniformsRef.current = uniforms;
    runtime.uniformsRef = uniforms;

    // Thin wrapper: delegates to extracted bakeCurrentToOld from BufferTransition
    const _bakeCurrentToOld = () => {
        if (!meshRef.current) return;
        bakeCurrentToOld(meshRef.current, uniforms.uLayoutMix.value);
    };

    const onBeforeCompile: ShaderMaterialProps['onBeforeCompile'] = useCallback((shader: any) => {
        applyShaderToMaterial(shader, uniforms);
        materialRef.current!.userData.shader = shader;
    }, [uniforms]);

     useEffect(() => {
        if (materialRef.current && materialRef.current.userData.shader) {
            const shader = materialRef.current.userData.shader;
            shader.uniforms.uColor1.value.set(color1);
            shader.uniforms.uColor2.value.set(color2);
            shader.uniforms.uColorMix.value = colorMix;
        }
    }, [color1, color2, colorMix, uniforms]);

    const atlasFaceMode = store((state) => state.atlasFaceMode);
    const lodThreshold = store((state) => state.lodThreshold);
    const lod2Threshold = store((state) => state.lod2Threshold);
    useEffect(() => {
        uniforms.uAtlasFaceMode.value = atlasFaceMode;
    }, [atlasFaceMode, uniforms]);
    useEffect(() => {
        uniforms.uLodThreshold.value = lodThreshold;
    }, [lodThreshold, uniforms]);
    useEffect(() => {
        uniforms.uLod2Threshold.value = lod2Threshold;
    }, [lod2Threshold, uniforms]);

    const fieldAnimStrength = store((s) => s.fieldAnimStrength);
    const waveSpeed = store((s) => s.waveSpeed);
    const waveAmplitude = store((s) => s.waveAmplitude);
    const breathingSpeed = store((s) => s.breathingSpeed);
    const breathingStrength = store((s) => s.breathingStrength);

    const waveDirX = store((s) => s.waveDirX);
    const waveDirY = store((s) => s.waveDirY);
    const waveDirZ = store((s) => s.waveDirZ);

    useEffect(() => {
        uniforms.uFieldAnimStrength.value = fieldAnimStrength;
        uniforms.uWaveSpeed.value = waveSpeed;
        uniforms.uWaveAmplitude.value = waveAmplitude;
        uniforms.uBreathingSpeed.value = breathingSpeed;
        uniforms.uBreathingStrength.value = breathingStrength;
        uniforms.uWaveDir.value.set(waveDirX, waveDirY, waveDirZ);
    }, [fieldAnimStrength, waveSpeed, waveAmplitude, breathingSpeed, breathingStrength, waveDirX, waveDirY, waveDirZ, uniforms]);

    // Data visualization: dataReveal + depthDirection via store, dataMode via ref (no re-render)
    const dataReveal = store((s) => s.dataReveal);
    const depthDirection = store((s) => s.depthDirection);
    const overrideFitMode = store((s) => s.overrideFitMode);
    const rippleStrength = store((s) => s.rippleStrength);
    const rippleFrequency = store((s) => s.rippleFrequency);
    const rippleSpeed = store((s) => s.rippleSpeed);
    useEffect(() => {
        uniforms.uDataReveal.value = dataReveal;
        uniforms.uDepthDirection.value = depthDirection;
        uniforms.uOverrideFitMode.value = overrideFitMode;
        uniforms.uRippleStrength.value = rippleStrength;
        uniforms.uRippleFrequency.value = rippleFrequency;
        uniforms.uRippleSpeed.value = rippleSpeed;
    }, [dataReveal, depthDirection, overrideFitMode, rippleStrength, rippleFrequency, rippleSpeed, uniforms]);

    // Update time uniform every frame for field animations
    useFrame((_, delta) => {
        uniforms.uTime.value += delta;
        runtime.currentTime = uniforms.uTime.value;
    });

    // Atlas texture loading — driven by Registry
    const atlasUrl = store((state) => state.atlasUrl);
    const activeAtlasId = store((state) => state.activeAtlasId);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        // Cleanup previous video
        if (videoRef.current) { videoRef.current.pause(); videoRef.current = null; }

        if (!atlasUrl) {
            uniforms.uUseAtlas.value = 0.0;
            uniforms.uLodEnabled.value = 0.0;
            uniforms.uLod2Enabled.value = 0.0;
            return;
        }

        // Disable LODs by default, enable only if registry has them
        uniforms.uLodEnabled.value = 0.0;
        uniforms.uLod2Enabled.value = 0.0;

        const isVideo = /\.(mp4|webm|mov)$/i.test(atlasUrl);
        const loader = new THREE.TextureLoader();
        const loadTex = (url: string): Promise<THREE.Texture> => new Promise((resolve) => {
            loader.load(url, (tex) => {
                tex.minFilter = THREE.LinearFilter;
                tex.magFilter = THREE.LinearFilter;
                tex.colorSpace = THREE.SRGBColorSpace;
                tex.generateMipmaps = false;
                // Eager GPU upload — pay cost now, not during first render
                gl.initTexture(tex);
                resolve(tex);
            });
        });

        // LOD 0: always load
        if (isVideo) {
            const video = document.createElement('video');
            video.src = atlasUrl;
            video.crossOrigin = 'anonymous';
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            video.autoplay = true;
            videoRef.current = video;
            video.addEventListener('canplay', () => {
                const tex = new THREE.VideoTexture(video);
                tex.minFilter = THREE.LinearFilter;
                tex.magFilter = THREE.LinearFilter;
                tex.colorSpace = THREE.SRGBColorSpace;
                uniforms.uAtlasTexture.value = tex;
                uniforms.uUseAtlas.value = 1.0;
                video.play();
            }, { once: true });
        } else {
            loadTex(atlasUrl).then((tex) => {
                uniforms.uAtlasTexture.value = tex;
                uniforms.uUseAtlas.value = 1.0;
            });
        }

        // LOD 1: load only if registry has ≥2 LOD levels
        const entry = store.getState().getActiveAtlas();
        if (entry && entry.lods.length >= 2) {
            const lod1 = entry.lods[1];
            const lod1Uniforms = [uniforms.uAtlasLod1_0, uniforms.uAtlasLod1_1, uniforms.uAtlasLod1_2, uniforms.uAtlasLod1_3];
            uniforms.uLod1Cols.value = lod1.cols;
            uniforms.uLod1TilesPerAtlas.value = lod1.tilesPerAtlas;
            Promise.all(lod1.urls.map(loadTex)).then((textures) => {
                textures.forEach((tex, i) => { if (lod1Uniforms[i]) lod1Uniforms[i].value = tex; });
                uniforms.uLodEnabled.value = 1.0;
            });
        }

        // LOD 2: config only — LodManager preloads all 16 atlases + gl.initTexture()
        if (entry && entry.lods.length >= 3) {
            const lod2 = entry.lods[2];
            uniforms.uLod2Cols.value = lod2.cols;
            uniforms.uLod2TilesPerAtlas.value = lod2.tilesPerAtlas;
        }

        // PAD atlas: auto-load per-tile aspect ratios + apply to mesh
        if (entry && entry.padMode && entry.manifestUrl) {
            store.getState().loadTileAspectData(entry.manifestUrl, meshRef.current || undefined);
        }

        return () => {
            if (videoRef.current) { videoRef.current.pause(); videoRef.current = null; }
        };
    }, [atlasUrl, activeAtlasId, uniforms, gl]);

    useEffect(() => {
        if (!meshRef.current) return;

        if (isInitialMount.current) {
            // First mount: set geometry, initialize, no animation
            meshRef.current.geometry = baseGeometry;
            initializeParticles(meshRef.current);

            // Initial mount: copy new → old directly (no interpolation needed)
            const copyBuf = (from: string, to: string) => {
                const f = meshRef.current!.geometry.getAttribute(from) as THREE.InstancedBufferAttribute;
                const t = meshRef.current!.geometry.getAttribute(to) as THREE.InstancedBufferAttribute;
                t.copyArray(f.array); t.needsUpdate = true;
            };
            copyBuf('aLayout', 'aOldLayout');
            copyBuf('aQuaternion', 'aOldQuaternion');
            copyBuf('aTarget', 'aOldTarget');
            // aTarget2 .zw = old is handled by initializeParticles (copies .xy → .zw before writing new .xy)

            uniforms.uLayoutMix.value = 1.0;
            meshRef.current.count = particleCount;
            prevCountRef.current = particleCount;
            isInitialMount.current = false;

            // Bind MorphShape to mesh + uniforms
            MorphShape.instance.bind(meshRef.current, uniforms);
            MorphShape.instance.notifyCountChange(0, particleCount);
            lastLayoutKey.current = `${layoutShape}_${particleCount}_${shapeSize}_${particleSize}_${layoutMode}_${galleryRedux}_${tileAspect}_${boxRatio}_${gapFactor}_${cornerRadius}_${tileAspectMode}_${layoutOrientation}_${layoutUpMode}_${layoutSpacingMode}_${layoutSpacing}_${spiralTurns}_${spiralGrowthRate}`;
        } else {
            // Fingerprint: only re-layout if something layout-defining actually changed
            const key = `${layoutShape}_${particleCount}_${shapeSize}_${particleSize}_${layoutMode}_${galleryRedux}_${tileAspect}_${boxRatio}_${gapFactor}_${cornerRadius}_${layoutOrientation}_${layoutUpMode}_${layoutSpacingMode}_${layoutSpacing}_${spiralTurns}_${spiralGrowthRate}`;
            if (key === lastLayoutKey.current) return;
            lastLayoutKey.current = key;
            if (runtime.dataIntelligenceLock) return;
            isTransitioning.current = true;

            const prevCount = prevCountRef.current;

            // Bake visually interpolated state → old buffers (seamless mid-flight transition)
            _bakeCurrentToOld();

            // Generate new layout (writes to aLayout, aQuaternion, aTarget, aTarget2)
            initializeParticles(meshRef.current);

            // During transition: render max(old, new) so shrinking/growing particles are visible
            meshRef.current.count = Math.max(prevCount, particleCount);

            // Notify MorphShape — dismisses selection, fires events
            MorphShape.instance.notifyMorphStart(prevCount, particleCount);

            // Animate everything: positions, rotations, AND morph params
            gsap.fromTo(uniforms.uLayoutMix,
                { value: 0 },
                {
                    value: 1,
                    duration: 1.5,
                    ease: 'power2.inOut',
                    onComplete: () => {
                        if (meshRef.current) {
                            meshRef.current.count = particleCount;
                        }
                        prevCountRef.current = particleCount;
                        isTransitioning.current = false;
                        MorphShape.instance.notifyMorphComplete(particleCount);
                    }
                }
            );
        }
    }, [baseGeometry, initializeParticles, particleCount, layoutShape, layoutMode, shapeSize, particleSize, galleryRedux, tileAspect, boxRatio, gapFactor, tileAspectMode, layoutOrientation, layoutUpMode, layoutSpacingMode, layoutSpacing, spiralTurns, spiralGrowthRate]);

    const canvas = store((s) => s.canvas);
    useEffect(() => {
        if (canvas) {
            initialize(canvas);
        }
        return () => {
            MorphShape.instance.unbind();
            unmount();
        };
    }, [canvas, initialize, unmount]);

    return (
        <instancedMesh
            ref={meshRef}
            args={[baseGeometry, undefined, MAX_PARTICLES]}
            frustumCulled={false}
        >
            <meshStandardMaterial
                ref={materialRef}
                transparent
                alphaTest={0.01}
                metalness={metalness}
                roughness={roughness}
                emissive={emissive}
                emissiveIntensity={emissiveIntensity}
                onBeforeCompile={onBeforeCompile}
            />
        </instancedMesh>
    );
};



// ClickPickerWrapper replaced by InteractionController + ClickPickerBridge (Step 4 refactor)


const DebugBBoxToggle: React.FC<{ meshRef: React.RefObject<THREE.InstancedMesh>, store: StoreType }> = ({ meshRef, store }) => {
    const showDebug = store((s) => s.showDebugBBox ?? false);
    const particleCount = store((s) => s.particleCount);
    const depthDirection = store((s) => s.depthDirection);
    const dataMode = store((s) => s.dataMode);
    const dataReveal = store((s) => s.dataReveal);
    if (!showDebug) return null;
    return (
        <DebugBBoxes
            meshRef={meshRef}
            particleCount={particleCount}
            depthDirection={depthDirection}
            dataMode={!!dataMode}
            dataReveal={dataReveal}
        />
    );
};

// ─── Error Boundary for safe component mounting ─────────────────────────────

class SafeMount extends React.Component<{ children: React.ReactNode; name: string }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };
    static getDerivedStateFromError(error: Error) { return { error }; }
    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error(`[SafeMount:${this.props.name}]`, error.message, info.componentStack);
    }
    render() {
        if (this.state.error) return null; // silently fail, error is logged
        return this.props.children;
    }
}

// ─── Selection Rope: catenary line from origin to popped-out particle ────────

// ─── Ghostbusters Beam: multiple wave-streams from nearby points to selected particle ───

const BEAM_COUNT = 4; // one per corner
const BEAM_SEGS = 48;

const SelectionRope: React.FC<{ meshRef: React.RefObject<THREE.InstancedMesh>, uniformsRef: React.MutableRefObject<any> }> = ({ meshRef, uniformsRef }) => {
    const selection = useParticleSelection();
    const geoRef = useRef<THREE.InstancedBufferGeometry | null>(null);
    const linesRef = useRef<LineSegment[]>([]);
    const builtRef = useRef(false);
    const cornersRef = useRef<THREE.Vector3[]>([]);

    const onGeo = useCallback((geo: THREE.InstancedBufferGeometry) => {
        geoRef.current = geo;
    }, []);

    const dummy = useMemo<LineSegment[]>(() => [{
        start: new THREE.Vector3(), end: new THREE.Vector3(0, 0.001, 0), thickness: 0,
    }], []);

    const _target = useMemo(() => new THREE.Vector3(), []);

    useFrame(() => {
        const geo = geoRef.current;
        if (!geo || !selection) {
            if (geo) geo.instanceCount = 0;
            builtRef.current = false;
            return;
        }

        if (!builtRef.current) {
            builtRef.current = true;

            const mesh = meshRef.current;
            const uniforms = uniformsRef.current;
            const corners: THREE.Vector3[] = [];

            if (mesh && uniforms) {
                const idx = selection.index;
                const targetAttr = mesh.geometry.getAttribute('aTarget') as THREE.InstancedBufferAttribute;
                const target2Attr = mesh.geometry.getAttribute('aTarget2') as THREE.InstancedBufferAttribute;

                // Read particle size from aTarget.x (sizeX) and aTarget2.y (sizeY)
                const sizeX = targetAttr ? targetAttr.getX(idx) * 0.5 : 0.1;
                const sizeY = target2Attr ? target2Attr.getY(idx) * 0.5 : 0.1;

                // Compute local right and up vectors from normal
                const n = selection.normal;
                const worldUp = Math.abs(n.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
                const right = new THREE.Vector3().crossVectors(worldUp, n).normalize();
                const up = new THREE.Vector3().crossVectors(n, right).normalize();

                // Scale from aLayout.w
                const layoutAttr = mesh.geometry.getAttribute('aLayout') as THREE.InstancedBufferAttribute;
                const scale = layoutAttr ? layoutAttr.getW(idx) : 1.0;
                const sx = sizeX * scale;
                const sy = sizeY * scale;

                // 4 corners of the tile face
                const pos = selection.position;
                corners.push(
                    new THREE.Vector3().copy(pos).addScaledVector(right, -sx).addScaledVector(up,  sy), // top-left
                    new THREE.Vector3().copy(pos).addScaledVector(right,  sx).addScaledVector(up,  sy), // top-right
                    new THREE.Vector3().copy(pos).addScaledVector(right,  sx).addScaledVector(up, -sy), // bottom-right
                    new THREE.Vector3().copy(pos).addScaledVector(right, -sx).addScaledVector(up, -sy), // bottom-left
                );
            }

            cornersRef.current = corners;

            const lines: LineSegment[] = [];
            for (let b = 0; b < BEAM_COUNT; b++) {
                const hue = b / BEAM_COUNT;
                lines.push({
                    start: new THREE.Vector3(),
                    end: new THREE.Vector3(),
                    startColor: new THREE.Color().setHSL(0.6 + hue * 0.12, 0.85, 0.55),
                    endColor: new THREE.Color().setHSL(0.65 + hue * 0.1, 0.7, 0.5),
                    startAlpha: 1.0,
                    endAlpha: 1.0,
                    thickness: 1.0,
                    segments: BEAM_SEGS,
                });
            }
            linesRef.current = lines;
        }

        // Target: the popped-out particle position
        _target.copy(selection.position).addScaledVector(selection.normal, 0.25);

        const lines = linesRef.current;
        for (let b = 0; b < Math.min(BEAM_COUNT, lines.length, cornersRef.current.length); b++) {
            lines[b].start.copy(cornersRef.current[b]);
            lines[b].end.copy(_target);
        }

        setLines(geo, lines);
    });

    return (
        <LinesAAMesh
            lines={dummy}
            baseRadius={0.005}
            opacity={1.0}
            fieldMode={1}
            fieldStrength={1.0}
            waveFreq={6.0}
            waveAmplitude={0.02}
            waveSpeed={8.0}
            waveLayers={3.0}
            depthWrite={false}
            blending={THREE.AdditiveBlending}
            onGeometryReady={onGeo}
        />
    );
};


const SHOW_BUCKET_BUTTONS_3D = false;
// ─── BucketButtons3D Bridge: subscribes to DataIntelligenceController state ──

const BucketButtons3DBridge: React.FC<{ diController: DataIntelligenceController, store: StoreType }> = ({ diController, store }) => {
    const [, setTick] = useState(0);
    const shapeSize = store((s) => s.shapeSize);

    // Subscribe to controller state changes via multi-listener pattern
    useEffect(() => {
        return diController.subscribe(() => setTick(t => t + 1));
    }, [diController]);

    // Ensure filter texture is assigned to uniforms (idempotent, covers timing edge cases)
    useEffect(() => {
        diController.initFilterTexture();
    }, [diController]);

    const handleSelect = useCallback((bucket: Bucket | null) => {
        // Re-init filter texture in case it wasn't ready before
        diController.initFilterTexture();
        diController.selectBucket(bucket);
    }, [diController]);

    return (
        <BucketButtons3D
            buckets={diController.buckets}
            activeBucket={diController.activeBucketLabel}
            onSelect={handleSelect}
            shapeSize={shapeSize}
        />
    );
};

/** Preloads all LOD2 textures + GPU upload. Passes them to LodManager. */
const Lod2Preloader: React.FC<{ meshRef: React.RefObject<THREE.InstancedMesh>, store: StoreType, uniformsRef: React.MutableRefObject<any>, particleCount: number }> = ({ meshRef, store, uniformsRef, particleCount }) => {
    const { gl } = useThree();
    const activeAtlasId = store((s) => s.activeAtlasId);
    const [lod2Textures, setLod2Textures] = useState<THREE.Texture[]>([]);

    useEffect(() => {
        const entry = store.getState().getActiveAtlas();
        if (!entry || entry.lods.length < 3) { setLod2Textures([]); return; }
        const lod2 = entry.lods[2];
        const loader = new THREE.TextureLoader();
        let loaded = 0;
        const textures: THREE.Texture[] = new Array(lod2.urls.length).fill(null);
        lod2.urls.forEach((url: string, i: number) => {
            loader.load(url, (tex) => {
                tex.minFilter = THREE.LinearFilter;
                tex.magFilter = THREE.LinearFilter;
                tex.colorSpace = THREE.SRGBColorSpace;
                tex.generateMipmaps = false;
                gl.initTexture(tex);
                textures[i] = tex;
                loaded++;
                if (loaded === lod2.urls.length) {
                    setLod2Textures(textures);
                    if (uniformsRef.current) uniformsRef.current.uLod2Enabled.value = 1.0;
                }
            });
        });
        return () => { textures.forEach(t => t?.dispose()); };
    }, [activeAtlasId, gl]);

    return <LodManager meshRef={meshRef} particleCount={particleCount} uniforms={uniformsRef.current} lod2Textures={lod2Textures} />;
};

const GPUParticleSystemCanvas: React.FC<{ store: StoreType, meshRef: React.RefObject<THREE.InstancedMesh>, uniformsRef: React.MutableRefObject<any>, interactionController: InteractionController, diController: DataIntelligenceController }> = ({ store, meshRef, uniformsRef, interactionController, diController }) => {
    const setCanvas = store((state) => state.setCanvas);
    const bgColor = store((state) => state.bgColor);
    const ambientIntensity = store((state) => state.ambientIntensity);
    const keyLightIntensity = store((state) => state.keyLightIntensity);
    const headLightIntensity = store((state) => state.headLightIntensity);
    const renderScale = store((state) => state.renderScale);
    const particleCount = store((state) => state.particleCount);
    const filterMatchIndices = store((s) => s.filterMatchIndices);
    const filterEnabled = store((s) => s.filterEnabled);
    const filterLineFace = store((s) => s.filterLineFace);

    return (
        <Canvas
            gl={{ antialias: true }}
            dpr={renderScale}
            onCreated={({ gl }) => { setCanvas(gl.domElement); }}
            camera={{ position: [0, 5, 12], fov: 60, near: 0.01, far: 5000 }}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        >
            <XRWrapper>
                <XRBackground color={bgColor} />
                <ambientLight intensity={ambientIntensity} />
                <hemisphereLight args={['#ffffff', '#444444', 0.4]} />
                <directionalLight position={[8, 10, 5]} intensity={keyLightIntensity} />
                <CameraLight intensity={headLightIntensity} />

                <GPUParticleInstances store={store} meshRef={meshRef} uniformsRef={uniformsRef} />
                <Lod2Preloader meshRef={meshRef} store={store} uniformsRef={uniformsRef} particleCount={particleCount} />
                <ClickPickerBridge controller={interactionController} meshRef={meshRef} store={store} uniformsRef={uniformsRef} />
                <DebugBBoxToggle meshRef={meshRef} store={store} />
                <SafeMount name="ParticleInfoCard"><ParticleInfoCard /></SafeMount>
                <SafeMount name="SelectionRope"><SelectionRope meshRef={meshRef} uniformsRef={uniformsRef} /></SafeMount>
                <SafeMount name="BucketFilterLinker">
                    <BucketFilterLinker
                        meshRef={meshRef}
                        uniformsRef={uniformsRef}
                        matchingIndices={filterMatchIndices}
                        enabled={filterEnabled}
                        lineFace={filterLineFace}
                    />
                </SafeMount>
                {/* 3D bucket buttons duplicate the chip row and collided with the bottom dock (screenshot 124999, 2026-09-13) — off by default */}
                {SHOW_BUCKET_BUTTONS_3D && <SafeMount name="BucketButtons3D">
                    <BucketButtons3DBridge diController={diController} store={store} />
                </SafeMount>}
                <SmoothZoomControls />
            </XRWrapper>
        </Canvas>
    );
};

const GPUParticlePage: React.FC<GPUParticlePageProps> = ({ store, autoLoadOneal }) => {
    const meshRef = useRef<THREE.InstancedMesh>(null!);
    const uniformsRef = useRef<any>(null);
    const diController = useMemo(() => {
        const c = new DataIntelligenceController();
        // React boundary: refs are resolved lazily so the controller never holds stale objects
        c.attach({
            store,
            getMesh: () => meshRef.current,
            getUniforms: () => uniformsRef.current,
        });
        return c;
    }, [store]);
    const interactionController = useMemo(() => new InteractionController(), []);
    const bgColor = store((state) => state.bgColor);

    // CameraIntelligence: create once, register on EngineRuntime
    const cameraIntelligence = useMemo(() => {
        const ci = new CameraIntelligence();
        runtime.cameraIntelligence = ci;
        return ci;
    }, []);

    // Create TileAccessor when mesh is ready and update on particle count changes
    const particleCount = store((s) => s.particleCount);
    const depthDirection = store((s) => s.depthDirection);
    const tileAccessorRef = useRef<InstancedMeshTileAccessor | null>(null);

    useEffect(() => {
        cameraIntelligence.setDepthDirection(depthDirection);
    }, [depthDirection, cameraIntelligence]);
    const dataMode = store((s) => s.dataMode);
    const dataReveal = store((s) => s.dataReveal);
    useEffect(() => {
        cameraIntelligence.setDataDepth(dataMode, dataReveal);   // camera aims at the extruded front face in data mode
    }, [dataMode, dataReveal, cameraIntelligence]);

    // The mesh mounts inside <Canvas> AFTER this effect's first run, so a one-shot check left CameraIntelligence
    // without a TileAccessor — flyToTile() then returns silently and the double-click did nothing (Alex 2026-09-13).
    // Retry until the mesh exists; the arrows' lazy init stays as belt and braces.
    useEffect(() => {
        let timer: number | null = null;
        const bind = () => {
            const mesh = meshRef.current; if (!mesh) return false;
            if (!tileAccessorRef.current) {
                tileAccessorRef.current = new InstancedMeshTileAccessor(mesh, particleCount);
                cameraIntelligence.setTileAccessor(tileAccessorRef.current);
            } else {
                tileAccessorRef.current.setCount(particleCount);
                tileAccessorRef.current.rebind(mesh);
            }
            return true;
        };
        if (!bind()) timer = window.setInterval(() => { if (bind() && timer !== null) { window.clearInterval(timer); timer = null; } }, 100);
        return () => { if (timer !== null) window.clearInterval(timer); };
    }, [particleCount, cameraIntelligence]);

    // Auto-load O'Neal products when autoLoadOneal is set
    const onealLoaded = useRef(false);
    useEffect(() => {
        // ?data=agentos[&face=card] opens the AgentOS federation directly (2026-09-16); /oneal keeps its prop
        const q = new URLSearchParams(window.location.search);
        const wanted = autoLoadOneal ? 'oneal' : q.get('data');
        if ((wanted !== 'oneal' && wanted !== 'agentos') || onealLoaded.current) return;
        const tryLoad = () => {
            if (meshRef.current && uniformsRef.current) {
                onealLoaded.current = true;
                if (wanted === 'agentos') diController.loadAgentOs(q.get('face') === 'card' ? 'card' : 'chat');
                else diController.loadOnealProducts();
            } else {
                setTimeout(tryLoad, 500);
            }
        };
        setTimeout(tryLoad, 1500);
    }, [autoLoadOneal, diController, store]);

    return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', background: bgColor }}>
            <GPUParticleSystemCanvas store={store} meshRef={meshRef} uniformsRef={uniformsRef} interactionController={interactionController} diController={diController} />
            <InfoCardModeSwitcher />
            <ARButton />
            <TileModeButtons store={store} />

            {/* Bottom dock (Alex 2026-09-13, screenshot 124999): one fixed container in normal flow — nothing overlaps.
                Row 1: bucket chips (scroll) · ◀ ▶ tile navigation · ⚙ layout settings.  Row 2: shape switcher (scroll). */}
            <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
                display: 'flex', flexDirection: 'column',
                background: 'rgba(8,12,24,0.92)', backdropFilter: 'blur(10px)',
                borderTop: '1px solid rgba(59,130,246,0.15)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 10px', borderBottom: '1px solid rgba(59,130,246,0.1)' }}>
                    <DataIntelligencePanel controller={diController} store={store} meshRef={meshRef} uniformsRef={uniformsRef} bucketsOnly />
                    <div style={{ flex: '0 0 auto', display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <button
                    onClick={() => {
                        // Lazy-init TileAccessor if not set yet (mesh may not have been ready on first useEffect)
                        if (meshRef.current && !tileAccessorRef.current) {
                            tileAccessorRef.current = new InstancedMeshTileAccessor(meshRef.current, store.getState().particleCount);
                            cameraIntelligence.setTileAccessor(tileAccessorRef.current);
                        }
                        cameraIntelligence.navigateSequential(-1);
                    }}
                    style={{
                        width: '44px', height: '44px', borderRadius: '50%',
                        background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(99,102,241,0.3)',
                        color: '#a78bfa', fontSize: '20px', cursor: 'pointer',
                        backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >◀</button>
                <button
                    onClick={() => {
                        if (meshRef.current && !tileAccessorRef.current) {
                            tileAccessorRef.current = new InstancedMeshTileAccessor(meshRef.current, store.getState().particleCount);
                            cameraIntelligence.setTileAccessor(tileAccessorRef.current);
                        }
                        cameraIntelligence.navigateSequential(1);
                    }}
                    style={{
                        width: '44px', height: '44px', borderRadius: '50%',
                        background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(99,102,241,0.3)',
                        color: '#a78bfa', fontSize: '20px', cursor: 'pointer',
                        backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >▶</button>
                    </div>
                    <LayouterSettingsOverlay store={store} inline />
                </div>
                <div style={{ display: 'flex', gap: '2px', padding: '6px 8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {(['box', 'sphere', 'plane', 'ring', 'cylinder', 'tube', 'helix', 'dna', 'galaxy', 'hexgrid', 'gallery', 'torus', 'line_h', 'line_v', 'ring_h', 'ring_v', 'masonry_v', 'masonry_h', 'spiral_a', 'sphere_grid_a', 'sphere_hex_a', 'plane_grid_a', 'plane_hex_a', 'cylinder_grid_a'] as const).map(shape => {
                    const current = store((s) => s.layoutShape);
                    const isActive = current === shape;
                    const isLibrary = shape.endsWith('_a');
                    const accentColor = isLibrary ? '168,85,247' : '59,130,246'; // purple for library, blue for engine
                    return (
                        <button
                            key={shape}
                            onClick={() => store.getState().setLayoutShape(shape)}
                            style={{
                                flex: '0 0 auto',
                                padding: '6px 10px',
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                fontWeight: isActive ? 700 : 400,
                                color: isActive ? '#f1f5f9' : (isLibrary ? '#a78bfa' : '#64748b'),
                                background: isActive ? `rgba(${accentColor},0.4)` : 'rgba(30,41,59,0.5)',
                                border: `1px solid ${isActive ? `rgba(${accentColor},0.6)` : `rgba(${accentColor},0.15)`}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {shape}
                        </button>
                    );
                })}
                </div>
            </div>

            {/* fixed top bar (data source, sort, dimension) — outside ResponsivePanelContainer: a CSS transform on the parent breaks position:fixed */}
            <DataIntelligencePanel controller={diController} store={store} meshRef={meshRef} uniformsRef={uniformsRef} overlayOnly />

            <ResponsivePanelContainer>
                <Panel style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
                    <UniversalUIPage yamlFile="gpu-particle-controls.yaml" store={store} meshRef={meshRef} />
                    <DataIntelligencePanel controller={diController} store={store} meshRef={meshRef} uniformsRef={uniformsRef} panelOnly />
                </Panel>
            </ResponsivePanelContainer>
        </div>
    );
};

export default GPUParticlePage; 